import { WSTableCellProps } from "@wingspanhq/fe-component-library";
import {
  Payable,
  PayeeEngagementType,
  PayStatement,
  WorkLog,
  WorkLogStatus
} from "@wingspanhq/wingspan-api-sdk";
import upperFirst from "lodash/upperFirst";
import { selectorWorkLogConvertedResourceLabels } from "../selectors/selectorWorkLogConvertedResourceLabels";
import { selectorWorkLogWorkerNoun } from "../selectors/selectorWorkLogWorkerNoun";
import { WORK_LOG_STATUS_DISCARDED } from "../types";

type GetWorkLogStatusCellPropsParams = {
  workLog: WorkLog;
  /** Set only for a contractor work log — a W-2 one converts into a statement. */
  payable?: Payable;
  /** Set only for a W-2 work log; carries the human-readable statement number. */
  payStatement?: PayStatement;
  /**
   * The approval window closed while the work log was still in review — see
   * `selectorWorkLogApprovalExpired`. Resolving it needs the work log's payroll
   * run, so only screens that already read one pass this.
   */
  isApprovalExpired?: boolean;
  /** Names the resource an unapproved work log will become. */
  engagementType?: PayeeEngagementType | null;
};

export function getWorkLogPayerStatusCellProps({
  workLog,
  payable,
  payStatement,
  isApprovalExpired,
  engagementType
}: GetWorkLogStatusCellPropsParams): WSTableCellProps {
  const convertedResourceNoun = selectorWorkLogConvertedResourceLabels({
    convertedResourceType: workLog.convertedResourceType,
    engagementType
  }).noun;
  const workerNoun = selectorWorkLogWorkerNoun(engagementType);

  // "Discarded" is not in the SDK WorkLogStatus enum yet. Neutral rather than an
  // error theme: an empty auto-submitted work log being discarded is the normal
  // outcome, not a failure.
  if ((workLog.status as string) === WORK_LOG_STATUS_DISCARDED) {
    return {
      secondaryText: `No ${convertedResourceNoun} created`,
      pill: {
        text: "Discarded",
        theme: "neutral",
        icon: "info-circle"
      }
    };
  }

  switch (workLog.status) {
    case WorkLogStatus.APPROVED:
      return {
        // A statement carries a human-readable number, so it is named outright.
        // TODO(v3): v3 Payable no longer exposes a friendly invoice number, so
        // the invoice label drops the "#<number>" suffix it showed on v1.
        secondaryText: payStatement
          ? `Pay statement #${payStatement.payStatementNumber}`
          : payable
            ? "Invoiced"
            : undefined,
        pill: {
          text: "Approved",
          theme: "success",
          icon: "check-circle"
        }
      };

    case WorkLogStatus.IN_REVIEW: {
      // Still "in review" server-side, but there is no run left to approve it
      // into — so it reads as its own terminal-ish state rather than as an
      // action the payer can still take.
      if (isApprovalExpired) {
        return {
          secondaryText: "Approval window expired",
          secondaryTextColor: "red400",
          pill: {
            text: "Expired",
            theme: "error",
            icon: "time"
          }
        };
      }

      const editingRequested = !!workLog.payeeOwnedData?.comment;
      return {
        secondaryText: editingRequested
          ? `${upperFirst(workerNoun)} requested editing`
          : "Awaiting your approval",
        secondaryTextColor: "amber400",
        pill: {
          text: "Action required",
          theme: "warning",
          icon: "alert"
        }
      };
    }

    case WorkLogStatus.IN_PROGRESS:
      return {
        secondaryText: "Awaiting resubmission",
        pill: {
          text: `Awaiting ${workerNoun}`,
          theme: "neutral",
          icon: "time"
        }
      };

    case WorkLogStatus.REJECTED:
      return {
        // TODO(v3): `invoiceId` renamed to `convertedResourceId` on v3 WorkLog.
        secondaryText: workLog.convertedResourceId
          ? `Cancelled ${convertedResourceNoun}`
          : `No ${convertedResourceNoun} created`,
        pill: {
          text: "Rejected",
          theme: "error",
          icon: "block"
        }
      };

    case WorkLogStatus.DRAFT:
    default:
      return {
        pill: {
          text: "Draft",
          theme: "neutral",
          icon: "draft"
        }
      };
  }
}
